import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { RestaurantProvider, useRestaurant } from './context/RestaurantContext';

import SignIn from './components/Auth/SignIn';
import ForgotPassword from './components/Auth/ForgotPassword';
import ProtectedRoute from './components/Auth/ProtectedRoute';

import RestaurantSelector from './pages/RestaurantSelector';
import RestaurantManagement from './pages/RestaurantManagement';
import RestaurantForm from './pages/RestaurantForm';
import Dashboard from './pages/Dashboard';
import EmployeeDashboard from './pages/EmployeeDashboard';
import Profile from './pages/Profile';
import UserManagement from './pages/UserManagement';
import UserForm from './pages/UserForm';

import './styles/theme.css';

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return null;
  if (isAuthenticated) return <Navigate to="/" replace />;
  return children;
};

const RootRoute = () => {
  const { user, loading, isAuthenticated } = useAuth();
  if (loading) return null;
  if (!isAuthenticated) return <SignIn />;
  if (user.role === 'employee') return <Navigate to="/my-dashboard" replace />;
  if (user.role === 'manager') return <Navigate to="/dashboard" replace />;
  return <Navigate to="/restaurants" replace />;
};

const RequireRestaurant = ({ children }) => {
  const { activeRestaurant } = useRestaurant();
  if (!activeRestaurant) return <Navigate to="/restaurants" replace />;
  return children;
};

const App = () => (
  <Router>
    <AuthProvider>
      <RestaurantProvider>
        <Toaster
          position="top-center"
          toastOptions={{
            duration: 3000,
            style: {
              background: '#0b0d12',
              color: '#fff',
              fontSize: 13,
              borderRadius: 12,
              padding: '10px 16px',
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 500
            }
          }}
        />
        <Routes>
          <Route path="/signin" element={<PublicRoute><SignIn /></PublicRoute>} />
          <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
          <Route path="/signup" element={<Navigate to="/signin" replace />} />

          <Route path="/" element={<RootRoute />} />

          {/* Owner only */}
          <Route path="/restaurants" element={
            <ProtectedRoute roles={['owner']}><RestaurantSelector /></ProtectedRoute>
          } />
          <Route path="/restaurants/manage" element={
            <ProtectedRoute roles={['owner']}><RestaurantManagement /></ProtectedRoute>
          } />
          <Route path="/restaurants/new" element={
            <ProtectedRoute roles={['owner']}><RestaurantForm /></ProtectedRoute>
          } />
          <Route path="/restaurants/:restaurantId/edit" element={
            <ProtectedRoute roles={['owner']}><RestaurantForm /></ProtectedRoute>
          } />

          {/* User management is OWNER-ONLY and requires an active restaurant. */}
          <Route path="/users" element={
            <ProtectedRoute roles={['owner']}>
              <RequireRestaurant><UserManagement /></RequireRestaurant>
            </ProtectedRoute>
          } />
          <Route path="/users/new" element={
            <ProtectedRoute roles={['owner']}>
              <RequireRestaurant><UserForm /></RequireRestaurant>
            </ProtectedRoute>
          } />
          <Route path="/users/:userId/edit" element={
            <ProtectedRoute roles={['owner']}>
              <RequireRestaurant><UserForm /></RequireRestaurant>
            </ProtectedRoute>
          } />

          {/* Owner + Manager */}
          <Route path="/dashboard" element={
            <ProtectedRoute roles={['owner', 'manager']}><Dashboard /></ProtectedRoute>
          } />

          {/* Employee only */}
          <Route path="/my-dashboard" element={
            <ProtectedRoute roles={['employee']}><EmployeeDashboard /></ProtectedRoute>
          } />

          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </RestaurantProvider>
    </AuthProvider>
  </Router>
);

export default App;